import {Card, CardBody, CardHeader, SelectItem} from "@heroui/react";
import {TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {addToast} from "@heroui/toast";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import {useWindowSize} from "@uidotdev/usehooks";
import React, {useState} from "react";
import {apiActionImproved} from "../../../api.ts";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import PInput from "../../../components/PInput.tsx";
import PSelect from "../../../components/PSelect.tsx";
import PTable from "../../../components/PTable.tsx";
import Header from "../../../Header.tsx";
import services from "../../../services";
import TournamentsRankingTablesList from "../../../tournaments/RankingTables/List.tsx";
import {RankingGroup} from "../../../types";
import {tableFullScreen} from "../../../components/tableStyles.ts";
import {maybeUndefinedDefaultKey} from "../../../utilities.ts";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/running/upload')({
    component: RouteComponent,
})

function RouteComponent() {
    const queryClient = useQueryClient();

    const [file, setFile] = useState<File | null>(null);
    // What we read from the chosen file before importing: its columns, a couple of example rows,
    // and which column we think feeds each field. The organizer confirms or corrects it.
    const [preview, setPreview] = useState<any>(null);
    const [mapping, setMapping] = useState<Record<string, string | null>>({});
    const [previewing, setPreviewing] = useState(false);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0] || null;
        setFile(picked);
        setPreview(null);
        if (!picked) return;

        setPreviewing(true);
        try {
            const fd = new FormData();
            fd.append('file', picked);
            const resp = await apiActionImproved('running-upload-preview' as any, {}, {body: fd});
            setPreview(resp);
            setMapping(resp?.mapping || {});
        } catch (err: any) {
            addToast({color: 'danger', title: err?.message || "Couldn't read that CSV."});
        }
        setPreviewing(false);
    };

    // Name can come from one column, or from first + last joined.
    const nameIsMapped = !!(mapping.name || mapping.firstname || mapping.lastname);
    const canImport = nameIsMapped && !!mapping.overallposition;

    const importsQuery = useQuery({
        queryKey: queryKeys.running.getImports(),
        queryFn:  async () => services.running.getImports(),
    })

    const rankingGroupsQuery = useQuery({
        queryKey: queryKeys.running.getRankings(),
        queryFn:  async () => services.running.getRankings(),
    });

    const handleUpload = async () => {
        if (file) {
            console.log('Uploading file...');

            const formData = new FormData();
            formData.append('file', file);
            // Send the confirmed mapping so any CSV layout imports, not just our exact columns.
            formData.append('mapping', JSON.stringify(mapping));

            try {
                const result = await apiActionImproved('running-upload' as any, {}, {
                    body: formData,
                });

                console.log(result);
                setFile(null);
                setPreview(null);

                addToast({
                    title: "Import successful",
                    color: "success",
                })
                queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})
            } catch (error: any) {
                console.log('error', error)
                if (error.message) {
                    addToast({
                        title: error.message,
                        color: "danger",
                    })
                } else {
                    alert("Error: " + error);
                }
            }
        }
    };

    const deleteMut = useMutation({
        mutationFn: services.running.deleteImport,
        onSuccess:  () => {
            queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})

            addToast({
                title: 'Removed',
                color: 'success',
            });
        },
        onError(error) {
            addToast({color: "danger", title: error?.message || "Something went wrong."});
        }
    })

    const updateMut = useMutation({
        mutationFn: services.running.updateImport,
        onSuccess:  () => {
            queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})

            addToast({
                title: 'Removed',
                color: 'success',
            });
        },
        onError(error) {
            addToast({color: "danger", title: error?.message || "Something went wrong."});
        }
    })

    const size = useWindowSize();

    const columns = (size.width && size.width < 1024
            ? [
                {key: 'combined', title: 'Import'},
                {key: 'actions', title: ''},
            ]
            : [
                {key: 'file_name', title: 'Import File'},
                {key: 'leaderboard', title: 'Leaderboard'},
                {key: 'created_at', title: 'Date'},
                {key: 'actions', title: ''},
            ]
    );

    async function startImportRename(imp) {
        const name = prompt('Enter race name', imp.event_title || imp.file_name);
        if (name !== null) {
            await updateImport(imp, {event_title: name});
            queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})
        }
    }

    async function changeImportLeaderboard(imp, leaderboard_uid: string) {
        await updateImport(imp, {ranking_group_uid: leaderboard_uid});
        queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})
    }

    async function updateImport(imp, fields) {
        await services.running.updateImport({
            uid:     imp.uid,
            version: imp.version + 1,
            ...fields,
        });
        queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})
    }

    return <>
        <Header backUrl={"/running"} />
        <Content>
            <Card className={"mb-4"}>
                <CardBody>
                    <TournamentsRankingTablesList title={<div className={"font-bold"}>Leaderboards</div>} sport_type={"running"} />
                </CardBody>
            </Card>

            <Card>
                <CardHeader className={"flex items-center justify-between"}>
                    <div className={"font-bold text-lg"}>Races</div>
                </CardHeader>
                <CardBody>

                    <Card className={"mb-4"}>
                        <CardHeader className={"flex items-center justify-between font-bold"}>
                            Import new results
                        </CardHeader>
                        <CardBody>
                            <div>
                                <div className={"mb-4"}>
                                    <PInput type={"file"} onChange={handleFileChange} />
                                </div>
                                {previewing && (
                                    <div className="mb-4 text-sm text-default-500">Reading your file…</div>
                                )}

                                {preview && (
                                    <Card className={"mb-4"}>
                                        <CardBody className="space-y-3">
                                            <div>
                                                <div className="font-bold">Match up your columns</div>
                                                <p className="text-sm text-default-500">
                                                    {preview.row_count} row{preview.row_count === 1 ? '' : 's'} found.
                                                    {preview.remembered
                                                        ? ' Using the same mapping as last time for this layout.'
                                                        : " We've guessed these from your headings — change any that are wrong."}
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                {Object.entries(preview.fields || {}).map(([field, label]: any) => {
                                                    const isRequired = (preview.required || []).includes(field);
                                                    // Name has a first/last fallback, so it's satisfied either way.
                                                    const missing = isRequired && (field === 'name' ? !nameIsMapped : !mapping[field]);
                                                    return (
                                                        <div key={field}>
                                                            <label className="mb-1 block text-xs uppercase tracking-wide text-default-400">
                                                                {label}{isRequired && <span className="text-danger"> *</span>}
                                                            </label>
                                                            <select
                                                                value={mapping[field] || ''}
                                                                onChange={(e) => setMapping({...mapping, [field]: e.target.value || null})}
                                                                className={`w-full rounded-lg border bg-content1 p-2 text-sm ${missing ? 'border-danger' : 'border-default-200'}`}
                                                            >
                                                                <option value="">— not in this file —</option>
                                                                {(preview.headers || []).map((h: string) => (
                                                                    <option key={h} value={h}>{h}</option>
                                                                ))}
                                                            </select>
                                                            {field === 'name' && !mapping.name && (mapping.firstname || mapping.lastname) && (
                                                                <p className="mt-1 text-xs text-default-500">
                                                                    Using {[mapping.firstname, mapping.lastname].filter(Boolean).join(' + ')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {preview.sample?.[0] && (
                                                <div className="overflow-x-auto rounded-lg bg-default-50 p-2 text-xs">
                                                    <div className="mb-1 font-semibold text-default-500">First row in your file</div>
                                                    {Object.entries(preview.sample[0]).slice(0, 8).map(([k, v]: any) => (
                                                        <span key={k} className="mr-3 inline-block">
                                                            <span className="text-default-400">{k}:</span> {String(v ?? '—')}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {!canImport && (
                                                <p className="text-sm text-danger">
                                                    Pick the columns holding the runner name and finishing position to continue.
                                                </p>
                                            )}
                                        </CardBody>
                                    </Card>
                                )}

                                {file && (
                                    <PButton
                                        color={"primary"}
                                        isDisabled={!!preview && !canImport}
                                        onPress={handleUpload}
                                    >Import</PButton>
                                )}
                            </div>
                        </CardBody>
                    </Card>


                    <PTable removeWrapper={true} classNames={tableFullScreen} isStriped={true} aria-label={"Running Results"}>
                        <TableHeader columns={columns}>
                            {(column) => <TableColumn key={column.key}>{column.title}</TableColumn>}
                        </TableHeader>
                        <TableBody>
                            {(importsQuery.data || []).map((result: any) => <TableRow key={result.uid}>
                                {(columnKey) => {
                                    if (columnKey === 'combined') {
                                        return <TableCell>
                                            Race: <strong>{result.event_title}</strong><br />
                                            <small>Import file: {result.file_name}</small>
                                            <br />
                                            <small>{result.created_at}</small>
                                        </TableCell>
                                    }
                                    if (columnKey === 'file_name') {
                                        return <TableCell>
                                            Race: <strong>{result.event_title || result.file_name}</strong>
                                            <PButton className={"ml-2"} size={"sm"} onPress={() => startImportRename(result)}>Rename</PButton>
                                            <br />
                                            <small>Import file: {result.file_name}</small>
                                            <br />
                                        </TableCell>
                                    }
                                    if (columnKey === 'leaderboard') {
                                        return <TableCell>
                                            <PSelect
                                                selectedKeys={maybeUndefinedDefaultKey(result.ranking_group_uid)}
                                                onSelectionChange={(e) => {
                                                    changeImportLeaderboard(result, e.currentKey as any)
                                                }}
                                                label={""}
                                                disallowEmptySelection={true}
                                            >
                                                {(rankingGroupsQuery.data || []).map((rankingGroup: RankingGroup) => (
                                                    <SelectItem key={rankingGroup.uid}>{rankingGroup.name}</SelectItem>
                                                ))}
                                            </PSelect>
                                        </TableCell>
                                    }
                                    if (columnKey === 'actions') {
                                        return <TableCell>
                                            <PButton color={"danger"} onPress={() => deleteMut.mutate(result.uid)}>
                                                Delete
                                            </PButton>
                                        </TableCell>
                                    }
                                    if (result.hasOwnProperty(columnKey)) {
                                        return <TableCell>
                                            {result[columnKey] || '-'}
                                        </TableCell>
                                    }
                                    return (
                                        <TableCell>
                                            {columnKey}
                                        </TableCell>
                                    );
                                }}
                            </TableRow>)}
                        </TableBody>

                    </PTable>
                </CardBody>
            </Card>
        </Content>
    </>
}
